/**
 * DentalChartV2.parts — presentational subcomponents extracted from
 * DentalChartV2.jsx to keep the main file focused on state + effects.
 *
 * Every export here is a stateless (or near-stateless) view. They receive
 * everything they need through props so they can be unit-tested and
 * re-used in future flows (e.g. a read-only chart on the patient portal).
 *
 * The `CATEGORIES` and `LEGEND` constants live here because the
 * BulkActionsPanel is the primary consumer of `CATEGORIES` (it renders
 * the same category grid as the single-tooth picker) and the main chart
 * imports them back.  Keeping them next to the components that render
 * them avoids drift when categories evolve.
 */
import { useState } from "react";
import {
  X, ChevronDown, MousePointerSquareDashed, Grid3X3, Eraser,
  FlipHorizontal2, ListChecks,
} from "lucide-react";
import { CONDITION_META_V2 as META, CHART_COLORS } from "@/components/app/ToothSVGV2";

// Phase-4 categorised picker — exact groupings from the spec.
export const CATEGORIES = [
  { key: "diagnosis",      label: "Diagnosis",      tint: CHART_COLORS.diagnosis,
    codes: ["caries", "fracture", "attrition", "mobility", "wear", "hypoplasia"] },
  { key: "restoration",    label: "Restoration",    tint: CHART_COLORS.restoration,
    codes: ["composite", "gic", "amalgam", "crown", "veneer", "onlay"] },
  { key: "endodontic",     label: "Endodontic",     tint: CHART_COLORS.rct,
    codes: ["rct", "pulpotomy", "pulpectomy"] },
  { key: "prosthodontics", label: "Prosthodontics", tint: CHART_COLORS.implant,
    codes: ["implant", "bridge", "pontic", "abutment"] },
  { key: "terminal",       label: "Terminal state", tint: CHART_COLORS.extraction,
    codes: ["extraction", "missing"] },
];

// Legend items — the reference shows exactly 10 in two rows.
export const LEGEND = [
  { code: "caries",     label: "Caries" },
  { code: "composite",  label: "Composite" },
  { code: "rct",        label: "Root Canal" },
  { code: "crown",      label: "Crown" },
  { code: "implant",    label: "Implant" },
  { code: "extraction", label: "Extraction" },
  { code: "bridge",     label: "Bridge" },
  { code: "filling",    label: "Filling" },
  { code: "sealant",    label: "Sealant" },
  { code: "missing",    label: "Missing" },
];

// ---------------------------------------------------------------------------
// Section — labeled block used inside the clinical panel.
// ---------------------------------------------------------------------------
export function Section({ label, children }) {
  return (
    <div className="mt-5">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">{label}</div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SurfaceBtn — the absolute-positioned surface toggle on the tooth diagram.
// ---------------------------------------------------------------------------
export function SurfaceBtn({ label, active, onClick, style, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      style={style}
      className={`absolute w-11 h-11 rounded-full grid place-items-center text-xs font-medium border transition ${
        active
          ? "bg-clay text-linen border-clay shadow"
          : "bg-white text-ink-mid border-[#E8EAE9] hover:bg-linen"
      }`}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// TBtn — pill button used across selection toolbar rows.  Kept tiny so
// re-using it in future toolbars (Treatment Plan, Rx, etc.) is trivial.
// ---------------------------------------------------------------------------
export function TBtn({ children, onClick, testid, tone }) {
  const cls =
    tone === "danger" ? "border-[#F1CDBF] text-[#8A3F1E] bg-white hover:bg-[#FDF3ED]" :
    tone === "ghost"  ? "border-[#E8EAE9] text-ink-mid bg-white hover:bg-linen"       :
                        "border-[#E8EAE9] text-ink bg-white hover:bg-linen";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center text-[11px] px-2.5 py-1 rounded-lg border transition ${cls}`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// SelectionToolbar — sits directly above the chart.  Every button emits
// an action on the parent's selection Set; the chart re-renders naturally
// because it reads from the same Set.  The counter chip on the right is
// click-to-expand and shows the FDI numbers of the currently-selected teeth
// with per-tooth remove buttons.
// ---------------------------------------------------------------------------
export function SelectionToolbar({
  count, counterOpen, onToggleCounter, selectedList, onRemoveOne,
  onQuadrant, onUpperArch, onLowerArch, onSelectAll, onInvert, onClear,
  onSmartCaries, onSmartRCT, onSmartCrown, onSmartImplant, onSmartMissing,
  onSmartFilled, onSmartFracture, onSmartMobility, onSmartHealthy, onSmartDeciduous,
}) {
  return (
    <div className="mb-4 rounded-2xl border border-[#E8EAE9] bg-linen/40 p-3"
         data-testid="v2-selection-toolbar">
      {/* Row 1: Quadrants / arches / all / invert / clear + counter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-ink-soft mr-1">Quadrant</span>
        <TBtn testid="sel-q1" onClick={() => onQuadrant("Q1")}>Upper R</TBtn>
        <TBtn testid="sel-q2" onClick={() => onQuadrant("Q2")}>Upper L</TBtn>
        <TBtn testid="sel-q3" onClick={() => onQuadrant("Q3")}>Lower L</TBtn>
        <TBtn testid="sel-q4" onClick={() => onQuadrant("Q4")}>Lower R</TBtn>
        <span className="mx-1 text-ink-soft">·</span>
        <TBtn testid="sel-upper-arch" onClick={onUpperArch}>Upper arch</TBtn>
        <TBtn testid="sel-lower-arch" onClick={onLowerArch}>Lower arch</TBtn>
        <TBtn testid="sel-all" onClick={onSelectAll}>
          <Grid3X3 className="w-3 h-3 mr-1" /> All teeth
        </TBtn>
        <span className="mx-1 text-ink-soft">·</span>
        <TBtn testid="sel-invert" onClick={onInvert}>
          <FlipHorizontal2 className="w-3 h-3 mr-1" /> Invert
        </TBtn>
        <TBtn testid="sel-clear" onClick={onClear} tone="danger">
          <Eraser className="w-3 h-3 mr-1" /> Deselect
        </TBtn>

        <div className="ml-auto relative">
          <button
            type="button"
            onClick={onToggleCounter}
            data-testid="sel-counter"
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition ${
              count > 0
                ? "bg-clay text-linen border-clay shadow"
                : "bg-white text-ink-mid border-[#E8EAE9]"
            }`}
          >
            <MousePointerSquareDashed className="w-3.5 h-3.5" />
            {count === 0 ? "Nothing selected" :
             count === 1 ? "1 tooth"          :
                           `${count} teeth`}
            {count > 0 && <ChevronDown className={`w-3 h-3 transition-transform ${counterOpen ? "rotate-180" : ""}`} />}
          </button>
          {counterOpen && count > 0 && (
            <div
              className="absolute right-0 mt-2 w-64 rounded-xl border border-[#E8EAE9] bg-white shadow-lg z-20 p-3"
              data-testid="sel-counter-popover"
            >
              <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Selected teeth</div>
              <div className="flex flex-wrap gap-1.5 max-h-52 overflow-y-auto">
                {selectedList.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-full bg-linen text-ink text-[11px] px-2 py-0.5"
                    data-testid={`sel-counter-item-${t}`}
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => onRemoveOne(t)}
                      className="text-ink-soft hover:text-ink"
                      aria-label={`Remove ${t}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Smart filters — filter selection by an existing chart condition. */}
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-ink-soft mr-1">Smart</span>
        <TBtn testid="smart-healthy"   onClick={onSmartHealthy}   tone="ghost">All Healthy</TBtn>
        <TBtn testid="smart-caries"    onClick={onSmartCaries}    tone="ghost">All Caries</TBtn>
        <TBtn testid="smart-rct"       onClick={onSmartRCT}       tone="ghost">Root Canal</TBtn>
        <TBtn testid="smart-crown"     onClick={onSmartCrown}     tone="ghost">Crowned</TBtn>
        <TBtn testid="smart-implant"   onClick={onSmartImplant}   tone="ghost">Implants</TBtn>
        <TBtn testid="smart-filled"    onClick={onSmartFilled}    tone="ghost">Filled</TBtn>
        <TBtn testid="smart-missing"   onClick={onSmartMissing}   tone="ghost">Missing</TBtn>
        <TBtn testid="smart-fracture"  onClick={onSmartFracture}  tone="ghost">Fractured</TBtn>
        <TBtn testid="smart-mobility"  onClick={onSmartMobility}  tone="ghost">Mobility</TBtn>
        <TBtn testid="smart-deciduous" onClick={onSmartDeciduous} tone="ghost">Deciduous</TBtn>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BulkActionsPanel — replaces the single-tooth clinical panel when 2+ teeth
// are selected.  Every "Apply / Remove" button loops over the whole
// selection via the existing per-tooth condition endpoints.  We intentionally
// REUSE the same CATEGORIES + META constants so the bulk picker stays in
// lock-step with the single-tooth picker.
// ---------------------------------------------------------------------------
export function BulkActionsPanel({ teeth, onClear, onInvert, onBulkAdd, onBulkRemove, onRemoveOne }) {
  const [mode, setMode] = useState("apply"); // "apply" | "remove"
  return (
    <div data-testid="v2-bulk-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-ink-soft">Bulk selection</div>
          <div className="font-display text-3xl leading-none text-ink" data-testid="v2-bulk-count">
            {teeth.length} teeth
          </div>
          <div className="text-[11px] text-ink-mid mt-1">
            Apply or remove conditions across every selected tooth.
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="v2-bulk-invert" onClick={onInvert}
                  className="w-7 h-7 rounded-full grid place-items-center text-ink-mid hover:bg-linen"
                  title="Invert selection">
            <FlipHorizontal2 className="w-4 h-4" />
          </button>
          <button data-testid="v2-bulk-clear" onClick={onClear}
                  className="w-7 h-7 rounded-full grid place-items-center text-ink-mid hover:bg-linen"
                  title="Clear selection">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Compact chip list of the selected FDI numbers with a remove button each */}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">Teeth</div>
        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-testid="v2-bulk-list">
          {teeth.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-full bg-linen text-ink text-[11px] px-2 py-0.5"
              data-testid={`v2-bulk-tooth-${t}`}
            >
              {t}
              <button onClick={() => onRemoveOne(t)}
                      className="text-ink-soft hover:text-ink" aria-label={`Remove ${t}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Apply / Remove mode toggle */}
      <div className="mt-5 inline-flex rounded-full bg-white border border-[#E8EAE9] p-0.5 text-xs" data-testid="v2-bulk-mode">
        <button
          type="button"
          onClick={() => setMode("apply")}
          data-testid="v2-bulk-mode-apply"
          className={`px-3 py-1 rounded-full transition ${mode === "apply" ? "bg-clay text-linen" : "text-ink-mid"}`}
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => setMode("remove")}
          data-testid="v2-bulk-mode-remove"
          className={`px-3 py-1 rounded-full transition ${mode === "remove" ? "bg-[#8A3F1E] text-white" : "text-ink-mid"}`}
        >
          Remove
        </button>
      </div>

      {CATEGORIES.map((cat) => (
        <div key={cat.key} className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-2">{cat.label}</div>
          <div className="grid grid-cols-2 gap-1.5">
            {cat.codes.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => (mode === "apply" ? onBulkAdd(code) : onBulkRemove(code))}
                data-testid={`v2-bulk-${mode}-${code}`}
                className="text-left text-xs px-2.5 py-1.5 rounded-lg border border-[#E8EAE9] hover:bg-linen transition inline-flex items-center gap-2"
              >
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: cat.tint }} />
                {META[code]?.label || code}
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-6 pt-3 border-t border-[#E8EAE9] text-[11px] text-ink-soft flex items-center gap-2">
        <ListChecks className="w-3.5 h-3.5" />
        Tip: Ctrl/Cmd + click adds to selection · Shift + click selects a range.
      </div>
    </div>
  );
}
